// Collegamento dell'app SPA al ponte (docs/PONTE.md): scarica le richieste di RVC e manda le risposte.
// Entra con il gettone di Firebase del telefono: il ponte controlla che il telefono sia approvato (ricevimento,
// responsabile, sviluppo). Le richieste si salvano in `ponte/<id di RVC>`; una risposta parte per RVC solo dopo
// che il database l'ha confermata (regola S1), con lo stesso id se si rimanda (niente doppioni).
import {
  doc, collection, query, where, onSnapshot, runTransaction, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { docArrivo, messaggioRisposta, risposta } from '../logica/ponte.js';

export const PONTE_URL = String(import.meta.env.VITE_PONTE_URL || '').replace(/\/+$/, '');
const OGNI = 60_000;

class ErrorePonte extends Error {
  constructor(stato) { super(`ponte ${stato}`); this.code = `ponte-${stato}`; }
}

/**
 * @param {{ db, auth, sid, uid, scatola }} ctx
 * @param {{ nome?: string, url?: string, ogni?: number }} [opz]
 */
export function creaPonte(ctx, { nome = '', url = PONTE_URL, ogni = OGNI } = {}) {
  const { db, auth, sid, uid, scatola } = ctx;
  const base = `strutture/${sid}`;
  let fermo = false;
  let inCorso = null;
  let ripeti = false; // arrivato altro da fare durante un giro: se ne fa subito un altro
  let richieste = [];
  const ascoltatori = new Set();
  const stato = { acceso: !!url, ok: null, ultimo: null, errore: null };

  async function chiama(metodo, percorso, corpo) {
    const token = await auth.currentUser.getIdToken();
    const r = await fetch(url + percorso, {
      method: metodo,
      headers: { authorization: `Bearer ${token}`, ...(corpo ? { 'content-type': 'application/json' } : {}) },
      body: corpo ? JSON.stringify(corpo) : undefined,
    });
    if (!r.ok) throw new ErrorePonte(r.status);
    return r.json();
  }

  // Una richiesta di RVC (anche cambiata o annullata): si salva una volta sola, con il suo evento nello storico.
  async function salvaArrivo(m) {
    const ref = doc(db, `${base}/ponte/${m.id}`);
    await runTransaction(db, async (tx) => {
      const prima = await tx.get(ref);
      const vecchio = prima.exists() ? prima.data() : null;
      const nuovo = docArrivo(m, vecchio);
      if (!nuovo) return;
      const ev = doc(collection(db, `${base}/eventi`));
      tx.set(ev, {
        tipo: 'ponte-arrivo', ponte: m.id, azione: nuovo.azione, versione: nuovo.versione,
        da: uid, daNome: String(nome).slice(0, 40), quando: serverTimestamp(),
      });
      tx.set(ref, { ...nuovo, arrivata: vecchio ? vecchio.arrivata : serverTimestamp(), ultimoEvento: ev.id, aggiornato: serverTimestamp() });
    });
  }

  async function scarica() {
    let ancora = true;
    while (ancora && !fermo) {
      const r = await chiama('GET', '/ponte/messaggi?nuovi=1&tipo=spa-prenotazione');
      for (const m of r.m || []) {
        await salvaArrivo(m);
        await chiama('POST', `/ponte/messaggi/${encodeURIComponent(m.id)}/stato`, { stato: 'arrivato', nome: String(nome).slice(0, 40) });
      }
      ancora = !!r.ancora;
    }
  }

  // Risposte confermate dal database e non ancora prese dal ponte.
  async function mandaRisposte() {
    for (const r of richieste.filter((x) => x.invio && !x.invio.mandato && !x.inSospeso)) {
      if (fermo) return;
      const esito = await chiama('POST', '/ponte/messaggi', { m: [messaggioRisposta(r.id, r, { nome })] });
      const e = esito.esiti?.[0]?.esito;
      if (e === 'nuovo' || e === 'aggiornato' || e === 'doppione') {
        await updateDoc(doc(db, `${base}/ponte/${r.id}`), { 'invio.mandato': true });
      } else {
        scatola?.registra(new Error(`ponte risposta ${esito.esiti?.[0]?.errore || 'sconosciuto'}`), { dove: 'ponte-risposta', richiesta: r.id });
      }
    }
  }

  // Un giro: scarica e manda. Mai due giri insieme. Senza rete non si prova (si riprova al ritorno della rete).
  function giro() {
    if (inCorso) { ripeti = true; return inCorso; }
    if (!url || fermo || !navigator.onLine || !auth.currentUser) return null;
    ripeti = false;
    inCorso = (async () => {
      try {
        await scarica();
        await mandaRisposte();
        Object.assign(stato, { ok: true, ultimo: Date.now(), errore: null });
      } catch (e) {
        Object.assign(stato, { ok: false, errore: e?.code || 'rete' });
        // Rete che cade a metà: non è un errore dell'app. Il resto (permessi, ponte rotto) va nella scatola nera.
        if (!(e instanceof TypeError) && navigator.onLine) scatola?.registra(e, { dove: 'ponte' });
      } finally {
        inCorso = null;
        if (!fermo) for (const f of ascoltatori) f({ ...stato });
        if (ripeti && stato.ok && !fermo) giro();
      }
    })();
    return inCorso;
  }

  const timer = url ? setInterval(giro, ogni) : null;
  const alRitorno = () => giro();
  const allaVista = () => { if (document.visibilityState === 'visible') giro(); };
  if (url) {
    window.addEventListener('online', alRitorno);
    document.addEventListener('visibilitychange', allaVista);
  }

  return {
    /** { acceso, ok, ultimo (ms), errore } dell'ultimo giro. */
    stato: () => ({ ...stato }),
    /** Avvisa a ogni giro finito. */
    ascolta(f) { ascoltatori.add(f); return () => ascoltatori.delete(f); },
    giro,
    /** Le richieste lette ora dal database (servono per sapere quali risposte mandare). */
    richieste(lista) {
      richieste = lista;
      if (lista.some((x) => x.invio && !x.invio.mandato && !x.inSospeso)) giro();
    },
    ferma() {
      fermo = true;
      ascoltatori.clear();
      if (timer) clearInterval(timer);
      window.removeEventListener('online', alRitorno);
      document.removeEventListener('visibilitychange', allaVista);
    },
  };
}

/**
 * Richieste dal giorno indicato in poi, in tempo reale. `inSospeso` = risposta salvata sul telefono e non ancora
 * confermata dal database (non si manda a RVC e non si mostra come fatta).
 */
export function ascoltaRichieste({ db, sid }, daGiorno, quando, errore) {
  const q = query(collection(db, `strutture/${sid}/ponte`), where('data', '>=', daGiorno));
  return onSnapshot(q, { includeMetadataChanges: true }, (s) => {
    quando(s.docs.map((d) => ({ id: d.id, ...d.data(), inSospeso: d.metadata.hasPendingWrites })), s.metadata.fromCache);
  }, errore);
}

/**
 * Aggiunge la risposta a un lotto di scritture (anche quello della prenotazione nuova: tutto insieme o niente).
 * @param {function} evento crea l'evento dello storico nel lotto: evento(b, tipo, extra) → ref
 * @returns {string} id dell'evento
 */
export function aggiungiRisposta(b, { db, sid }, r, stato, { proposta = null, prenotazioni = null, evento }) {
  const patch = risposta(r, stato, proposta, prenotazioni);
  const ev = evento(b, 'ponte-risposta', { ponte: r.id, stato, ...(patch.proposta ? { proposta: patch.proposta } : {}) });
  b.set(doc(db, `strutture/${sid}/ponte/${r.id}`), { ...patch, ultimoEvento: ev.id, aggiornato: serverTimestamp() }, { merge: true });
  return ev.id;
}
